import type { PulseState } from './useSequentialPulse';

/**
 * Phase 36.5 — Node Pulse State Resolver
 * 
 * Determines the visual pulse state of a node based on the
 * sequential execution pulse engine. Used by all node components.
 * 
 * Returns one of three states:
 * - 'active'  — This node is currently being executed (heavy neon glow)
 * - 'trail'   — This node was previously visited (subtle residual glow)
 * - 'idle'    — This node is not part of the current execution
 */

export type NodePulseLevel = 'active' | 'trail' | 'idle';

export function getNodePulseLevel(
  nodeId: string,
  pulseState?: PulseState,
  legacyActiveNodeIds?: string[]
): NodePulseLevel {
  // Phase 36.5: Sequential pulse state (primary)
  if (pulseState?.isPulsing) {
    if (pulseState.currentNodeId === nodeId) return 'active';
    if (pulseState.visitedNodeIds?.includes(nodeId)) return 'trail';
  }

  // Legacy: flat array fallback
  if (legacyActiveNodeIds?.includes(nodeId)) return 'trail';

  return 'idle';
}

/**
 * Returns the Tailwind CSS classes for a node's border/ring/glow
 * based on its current pulse level.
 * 
 * ACTIVE:  Bright cyan neon — scale effect, heavy glow ring
 * TRAIL:   Subtle emerald — mild glow, no scale
 * IDLE:    Returns empty string (node uses its own default styling)
 */
export function getNodePulseClasses(level: NodePulseLevel): string {
  switch (level) {
    case 'active':
      return 'pulse-node-active';
    case 'trail':
      return 'pulse-node-trail';
    case 'idle':
    default:
      return '';
  }
}

/**
 * Checks if a given node has an active, visually valid connection (edge)
 * to a LunaCoreNode (LUNA_CORE_HUB) in the ReactFlow graph.
 */
export function isNodeWiredToLunaCore(
  nodeId: string,
  edges: any[] = [],
  nodes: any[] = []
): boolean {
  if (!edges || edges.length === 0) return false;
  const coreNodeIds = new Set(
    nodes
      .filter((n) => n?.type === 'lunaCoreNode' || n?.type === 'LUNA_CORE_HUB' || n?.id?.toLowerCase().includes('core'))
      .map((n) => String(n.id))
  );

  const targetNode = nodes.find((n) => String(n.id) === String(nodeId));
  const nodeType = (targetNode?.type || '').toLowerCase();
  const rawId = String(nodeId).toLowerCase();

  return edges.some((e) => {
    const isSource = String(e.source) === String(nodeId) && coreNodeIds.has(String(e.target));
    const isTarget = String(e.target) === String(nodeId) && coreNodeIds.has(String(e.source));

    if (!isSource && !isTarget) return false;

    const sH = String(e.sourceHandle || '');
    const tH = String(e.targetHandle || '');

    // Reject broken legacy / corrupted generic handles on specialized nodes
    if (sH === 'out-core' && tH === 'in-core') {
      if (nodeType.includes('callguard') || rawId.includes('callguard')) return false;
      if (nodeType.includes('knowledge') || rawId.includes('knowledge')) return false;
      if (nodeType.includes('whatsapp') || rawId.includes('whatsapp')) return false;
      if (nodeType.includes('action') || rawId.includes('action')) return false;
      if (nodeType.includes('security') || rawId.includes('security')) return false;
      if (nodeType.includes('intent') || rawId.includes('intent')) return false;
      if (nodeType.includes('media') || rawId.includes('media')) return false;
      if (nodeType.includes('catalog') || rawId.includes('catalog')) return false;
      if (nodeType.includes('followup') || rawId.includes('followup')) return false;
      if (nodeType.includes('review') || rawId.includes('review')) return false;
      if (nodeType.includes('webwidget') || nodeType.includes('web_widget') || rawId.includes('webwidget') || rawId.includes('web_widget')) return false;
      if (nodeType.includes('email') || rawId.includes('email')) return false;
      if (nodeType.includes('database') || rawId.includes('database')) return false;
      if (nodeType.includes('transfer') || rawId.includes('transfer')) return false;
      if (nodeType.includes('scraper') || rawId.includes('scraper')) return false;
      if (nodeType.includes('instruction') || nodeType.includes('persona') || rawId.includes('instruction') || rawId.includes('persona')) return false;
      if (nodeType.includes('memory') || rawId.includes('memory')) return false;
      if (nodeType.includes('section') || rawId.includes('section')) return false;
      if (nodeType.includes('permission') || rawId.includes('permission')) return false;
      if (nodeType.includes('sipcall') || nodeType.includes('sip_call') || rawId.includes('sipcall') || rawId.includes('sip_call')) return false;
      if (nodeType.includes('auth') || rawId.includes('auth')) return false;
    }
    if (sH === 'out-form-gen' && tH === 'in-core') return false;

    return true;
  });
}

